import * as React from "react";
import { ErrorPage, MissingPage } from "./MiscPages";
import { createHashRouter, RouterProvider } from "react-router-dom";
import FullScreenSpinner from "./components/FullScreenSpinner";
import { DefaultThemeProvider } from "./components/ThemeProviderFactory";
import {
    withMarketplaceTheme,
    newTransactionWorkflowOptionsBuilder,
    continueTransactionWorkflowOptionsBuilder,
    newBundleOptionsBuilder,
    existingBundleOptionsBuilder,
} from "../scenes/MarketplaceThemes/hook";

import "@aws-amplify/ui-react/styles.css";
import { ProgressiveWebApp } from "../scenes/ProgressiveWebApp";
import { Drawer, DrawerProvider } from "../shared/presentation/subcomponent/Drawer";
import { ToastProvider } from "../shared/presentation/subcomponent/Toast";
import { useCallback, useEffect, useState } from "react";
import { InformationMessageType, toast, ToastCallback } from "@spiffcommerce/core";
import { Alert, Snackbar } from "@mui/material";
import { withProfiler, wrapCreateBrowserRouterV6 } from "@sentry/react";

const BundleProviderAmendFlow = React.lazy(() => import("../scenes/wrappers/BundleProviderAmendFlow"));
const ExperienceProviderShareFlow = React.lazy(() => import("../scenes/wrappers/ExperienceProviderShareFlow"));
const BundleProviderShareFlow = React.lazy(() => import("../scenes/wrappers/BundleProviderShareFlow"));
const BundleProviderQuoteFlow = React.lazy(() => import("../scenes/wrappers/BundleProviderQuoteFlow"));
const ExperienceProviderAmendFlow = React.lazy(() => import("../scenes/wrappers/ExperienceProviderAmendFlow"));

const sentryCreateHashRouter = wrapCreateBrowserRouterV6(createHashRouter);

export const routes = [
    { path: "experience/new", Component: withMarketplaceTheme(newTransactionWorkflowOptionsBuilder) },
    { path: "experience/:transactionId/amend", Component: ExperienceProviderAmendFlow },
    {
        path: "experience/:transactionId",
        Component: withMarketplaceTheme(continueTransactionWorkflowOptionsBuilder),
    },
    { path: "experience/:transactionId/share", Component: ExperienceProviderShareFlow },
    { path: "bundle/new", Component: withMarketplaceTheme(newBundleOptionsBuilder) },
    { path: "bundle/:bundleId/amend", Component: BundleProviderAmendFlow },
    { path: "bundle/:bundleId", Component: withMarketplaceTheme(existingBundleOptionsBuilder) },
    { path: "bundle/:bundleId/share", Component: BundleProviderShareFlow },
    { path: "bundle/:bundleId/quote", Component: BundleProviderQuoteFlow },
    { path: "pwa", Component: ProgressiveWebApp },
    {
        path: "*",
        Component: MissingPage,
    },
];

const router = sentryCreateHashRouter([
    {
        path: "/",
        errorElement: <ErrorPage />,
        children: routes,
    },
]);

const ApplicationIndex: React.FunctionComponent = () => {
    const [toastMessage, setToastMessage] = useState<null | string>(null);
    const [toastType, setToastType] = useState<null | InformationMessageType>(null);

    useEffect(() => {
        const callback: ToastCallback = (callbackOptions) => {
            setToastMessage(callbackOptions.toastMessage);
            setToastType(callbackOptions.toastType);
        };
        toast.addToastCallback(callback);
        return () => {
            toast.removeToastCallback(callback);
        };
    }, []);

    const handleSnackbarClose = useCallback(() => {
        toast.setLatestToast(null, null);
    }, []);

    const toastSwitch = useCallback(() => {
        switch (toastType) {
            case InformationMessageType.Error:
                return (
                    <Alert variant="filled" severity="error">
                        {toastMessage}
                    </Alert>
                );
            case InformationMessageType.Warning:
                return (
                    <Alert variant="filled" severity="warning">
                        {toastMessage}
                    </Alert>
                );
            case InformationMessageType.Info: // TODO: Split info type into info and success?
                return (
                    <Alert variant="filled" severity="success">
                        {toastMessage}
                    </Alert>
                );
            default:
                return;
        }
    }, [toastMessage, toastType]);

    return (
        <React.Suspense fallback={<FullScreenSpinner />}>
            <DefaultThemeProvider isDark={false}>
                <div className="application">
                    <Snackbar
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                        open={!!toastMessage}
                        message={toastMessage}
                        autoHideDuration={8000}
                        onClose={handleSnackbarClose}
                    >
                        {toastSwitch()}
                    </Snackbar>
                    <ToastProvider vertical="bottom" horizontal="left" timeout={8000}>
                        <DrawerProvider>
                            <RouterProvider router={router} />
                            <Drawer />
                        </DrawerProvider>
                    </ToastProvider>
                </div>
            </DefaultThemeProvider>
        </React.Suspense>
    );
};

export default withProfiler(ApplicationIndex);
